<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import { computed, onMounted, reactive, ref } from 'vue'
import { ExistingDhwTankType } from '@/helper/Types'
import formStore from '@/store/FormStore'
import FormElement from '@/components/FormElement.vue'
import InputSuffix from '@/components/InputSuffix.vue'
import HintBox from '@/components/HintBox.vue'
import { debounce } from '@/helper/tools'
import userStore from '@/store/UserStore'

const props = defineProps<{
  validator: ValidatorType,
}>()

defineEmits(['selectButtonClicked'])

const showHintBox = ref<boolean>(false)
const existingDhwTank = reactive<ExistingDhwTankType>({
  name: (formStore.data.existingDhwTank as ExistingDhwTankType)?.name || '',
  volume: (formStore.data.existingDhwTank as ExistingDhwTankType)?.volume || ''
})

const dhwTankVolumeIsSufficient = computed(() => {
  return existingDhwTank.volume && parseInt(existingDhwTank.volume) >=
    ((projectStore.projectProperties.dhwPersons.val || 0) * (projectStore.projectProperties.dhwDemand.val || 0) * userStore.dhwMixingLoss)
})

onMounted(() => showHintBox.value = shouldShowHint())

function shouldShowHint() {
  return !!existingDhwTank.volume && !dhwTankVolumeIsSufficient.value
}

function onStorageVolumeChange() {
  updateExistingDhwTank()

  if (dhwTankVolumeIsSufficient.value || !existingDhwTank.volume) {
    showHintBox.value = false
  } else {
    debounce(() => showHintBox.value = shouldShowHint(), 1500)()
  }
}

function updateExistingDhwTank() {
  formStore.data.existingDhwTank = existingDhwTank
}

defineExpose({
  validator: props.validator
})
</script>

<template>
  <div id="existing-dhw-tank" class="steps-table__excluded-info excluded-info card">
    <div class="wrapper-row">
      <div class="col-md-6">
        <FormElement
          v-validate="projectStore.projectProperties.existingDhwTank.formValidationRule?.name"
          :error="validator.existingDhwTankName?.error"
          :label="$t('step_components.existing_dhw_tank_name')"
          :required="true"
        >
          <input
            id="existing-dhw-tank-name"
            v-model="existingDhwTank.name"
            :placeholder="$t('step_components.existing_dhw_tank_name_placeholder')"
            data-hj-suppress
            name="existing-dhw-tank-name"
            type="text"
            @input="updateExistingDhwTank"
          >
        </FormElement>
      </div>

      <div class="col-md-6">
        <FormElement
          v-validate="projectStore.projectProperties.existingDhwTank.formValidationRule?.volume"
          :error="validator.existingDhwTankVolume?.error"
          :label="$t('step_components.existing_dhw_tank_volume')"
        >
          <InputSuffix
            id="existing-dhw-tank-volume"
            v-model="existingDhwTank.volume"
            :placeholder="$t('step_components.existing_dhw_tank_volume_placeholder')"
            suffix=" l"
            type="text"
            @input="onStorageVolumeChange"
          />
        </FormElement>
      </div>
    </div>

    <HintBox
      v-if="showHintBox"
      :btn-text="$t('step_components.existing_dhw_tank.hint_btn_text')"
      :description="$t('step_components.existing_dhw_tank.hint_description')"
      :title="$t('step_components.existing_dhw_tank.hint_title')"
      @btn-clicked="$emit('selectButtonClicked')"
    />
  </div>
</template>

<style lang="scss" scoped>
#existing-dhw-tank {
  gap: 1rem;
}

.excluded-info {
  align-items: start;
  flex-direction: column;

  .wrapper-row {
    display: flex;
    width: 100%;
    gap: 1rem;
  }

  [class^='col-md-'] {
    padding-left: 0;
    padding-right: 0;
    flex: 1;
  }
}

.inputsuffix__wrapper {
  width: 100%;
}

:deep(.form__input-wrapper) {
  height: rem-calc(40);
}

label.form__checkbox {
  margin-bottom: 0;
}

.form__element {
  margin-bottom: 0;
  margin-top: 0;
}
</style>
